import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  Box, Button, Typography, IconButton, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Grid, Alert, Snackbar,
  FormControl, InputLabel, Select, MenuItem, Skeleton, InputAdornment,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Chip, Stack, Autocomplete
} from '@mui/material';
import { Add, Edit, Delete, Search, Group, Clear, Close, Groups, Business, Person, Computer } from '@mui/icons-material';
import { createTeam, updateTeam, deleteTeam } from '../api/services/userService/Team';
import NodeAssignmentDialog from './NodeAssignmentDialog';

export default function TeamManagement({ userPermissions, sharedData, onRefresh }) {
  const { keycloak } = useKeycloak();
  const [teams, setTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openNodeDialog, setOpenNodeDialog] = useState(false);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState({ name: '', departmentId: '', parentId: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const canManage = useCallback(() => {
    if (!userPermissions) return false;
    return userPermissions.isSuperAdmin ||
      Object.values(userPermissions.clientRoles || {}).includes('admin') ||
      Object.values(userPermissions.departmentRoles || {}).includes('admin') ||
      Object.values(userPermissions.teamRoles || {}).some(role => ['lead', 'admin'].includes(role));
  }, [userPermissions]);

  const filterByPermissions = useCallback((list, type) => {
    if (!userPermissions || userPermissions.isSuperAdmin) return list;
    switch (type) {
      case 'clients':
        return list.filter(c => userPermissions.clientIds?.includes(c.id));
      case 'departments':
        return list.filter(d =>
          userPermissions.departmentIds?.includes(d.id) ||
          userPermissions.clientIds?.includes(d.clientId)
        );
      case 'teams':
        return list.filter(t =>
          userPermissions.teamIds?.includes(t.id) ||
          userPermissions.departmentIds?.includes(t.departmentId)
        );
      default:
        return list;
    }
  }, [userPermissions]);

  const filteredDepartments = useMemo(() => {
    let result = filterByPermissions(sharedData?.departments || [], 'departments');
    if (selectedClientId) {
      result = result.filter(d => d.clientId === selectedClientId);
    }
    return result;
  }, [sharedData?.departments, selectedClientId, filterByPermissions]);

  const filteredTeams = useMemo(() => {
    let result = filterByPermissions(sharedData?.teams || [], 'teams');
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(t => t.name?.toLowerCase().includes(term));
    }
    if (selectedDepartmentId) {
      result = result.filter(t => t.departmentId === selectedDepartmentId);
    } else if (selectedClientId) {
      const deptIds = filteredDepartments.map(d => d.id);
      result = result.filter(t => deptIds.includes(t.departmentId));
    }
    return result;
  }, [sharedData?.teams, searchTerm, selectedDepartmentId, selectedClientId, filteredDepartments, filterByPermissions]);

  const paginatedTeams = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredTeams.slice(start, start + rowsPerPage);
  }, [filteredTeams, page, rowsPerPage]);

  useEffect(() => {
    if (sharedData) {
      setClients(filterByPermissions(sharedData.clients || [], 'clients'));
      setDepartments(filterByPermissions(sharedData.departments || [], 'departments'));
      setAllTeams(filterByPermissions(sharedData.teams || [], 'teams'));
      setUsers(sharedData.users || []);
      setTeams(paginatedTeams);
    }
  }, [sharedData, paginatedTeams, filterByPermissions]);

  const getDepartmentName = (deptId) => departments.find(d => d.id === deptId)?.name || '—';

  const getClientName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    if (!dept) return '—';
    return clients.find(c => c.id === dept.clientId)?.name || '—';
  };

  const getLeadName = (leadId) => {
    if (!leadId) return null;
    const user = users.find(u => u.id === leadId);
    return user?.full_name || user?.fullName || user?.email;
  };

  const openCreateDialog = () => {
    setIsEdit(false);
    setFormData({ name: '', departmentId: '', parentId: '' });
    setOpenDialog(true);
  };

  const openEditDialog = (team) => {
    setIsEdit(true);
    setCurrentTeam(team);
    setFormData({ name: team.name, departmentId: team.departmentId, parentId: team.parentId || '' });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      setNotification({ open: true, message: 'Name required', severity: 'error' });
      return;
    }
    if (!formData.departmentId) {
      setNotification({ open: true, message: 'Select a department', severity: 'error' });
      return;
    }

    try {
      await keycloak.updateToken(30);

      const data = { name: formData.name, departmentId: formData.departmentId };
      if (formData.parentId) data.parentId = formData.parentId;

      const res = isEdit
        ? await updateTeam({ id: currentTeam.id, ...data }, keycloak.token)
        : await createTeam(data, keycloak.token);

      if (res.success) {
        setNotification({ open: true, message: `Team ${isEdit ? 'updated' : 'created'}`, severity: 'success' });
        if (onRefresh) await onRefresh();
        setOpenDialog(false);
      } else {
        setNotification({ open: true, message: res.error || 'Failed', severity: 'error' });
      }
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await keycloak.updateToken(30);
      const res = await deleteTeam(currentTeam.id, keycloak.token);
      if (res.success) {
        setNotification({ open: true, message: 'Deleted', severity: 'success' });
        if (onRefresh) await onRefresh();
      } else {
        setNotification({ open: true, message: res.error || 'Failed', severity: 'error' });
      }
      setOpenDeleteDialog(false);
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedClientId('');
    setSelectedDepartmentId('');
    setPage(0);
  };

  const hasFilters = searchTerm || selectedClientId || selectedDepartmentId;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>Teams</Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredTeams.length} team{filteredTeams.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        {canManage() && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog} size="small">
            Add Team
          </Button>
        )}
      </Box>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
            sx={{ width: 180 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}><Clear fontSize="small" /></IconButton>
                </InputAdornment>
              )
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Client</InputLabel>
            <Select
              value={selectedClientId}
              label="Client"
              onChange={e => { setSelectedClientId(e.target.value); setSelectedDepartmentId(''); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {clients.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={!selectedClientId}>
            <InputLabel>Department</InputLabel>
            <Select
              value={selectedDepartmentId}
              label="Department"
              onChange={e => { setSelectedDepartmentId(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {filteredDepartments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </Select>
          </FormControl>
          {hasFilters && (
            <Button size="small" onClick={clearFilters} startIcon={<Clear />}>Clear</Button>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'action.hover' } }}>
              <TableCell>Name</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Lead</TableCell>
              {canManage() && <TableCell align="right" sx={{ width: 140 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton width={150} /></TableCell>
                  <TableCell><Skeleton width={120} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  {canManage() && <TableCell><Skeleton width={60} /></TableCell>}
                </TableRow>
              ))
            ) : teams.length > 0 ? (
              teams.map(team => (
                <TableRow key={team.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 32, height: 32, borderRadius: 1,
                          bgcolor: 'info.main',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >
                        <Group sx={{ fontSize: 18, color: 'white' }} />
                      </Box>
                      <Typography variant="body2" fontWeight={500}>{team.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<Groups sx={{ fontSize: '14px !important' }} />}
                      label={getDepartmentName(team.departmentId)}
                      size="small"
                      variant="outlined"
                      sx={{ height: 24 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {getClientName(team.departmentId)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {team.leadId ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {getLeadName(team.leadId)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  {canManage() && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setCurrentTeam(team);
                          setOpenNodeDialog(true);
                        }}
                        title="Manage Nodes"
                      >
                        <Computer fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => openEditDialog(team)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => { setCurrentTeam(team); setOpenDeleteDialog(true); }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={canManage() ? 5 : 4} sx={{ py: 8, textAlign: 'center' }}>
                  <Group sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No teams found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredTeams.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
      />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit' : 'New'} Team</Typography>
            <IconButton onClick={() => setOpenDialog(false)} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              fullWidth size="small" label="Name" required
              value={formData.name}
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
            />
            <FormControl fullWidth size="small" required>
              <InputLabel>Department</InputLabel>
              <Select
                value={formData.departmentId}
                label="Department"
                onChange={e => setFormData(p => ({ ...p, departmentId: e.target.value }))}
              >
                {departments.map(d => {
                  const client = clients.find(c => c.id === d.clientId);
                  return <MenuItem key={d.id} value={d.id}>{d.name} {client && `(${client.name})`}</MenuItem>;
                })}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Parent Team</InputLabel>
              <Select
                value={formData.parentId}
                label="Parent Team"
                onChange={e => setFormData(p => ({ ...p, parentId: e.target.value }))}
              >
                <MenuItem value="">None</MenuItem>
                {allTeams.filter(t => t.id !== currentTeam?.id).map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>{isEdit ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs">
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{currentTeam?.name}</strong>? Members will be removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <NodeAssignmentDialog
        open={openNodeDialog}
        onClose={() => setOpenNodeDialog(false)}
        entityType="team"
        entityId={currentTeam?.id}
        entityName={currentTeam?.name}
        onSuccess={onRefresh}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotification(p => ({ ...p, open: false }))} severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
